export interface SEOConfig {
    title?: string;
    description?: string;
    keywords?: string;
}

const DEFAULT_TITLE = 'vivo社区App - 与热爱同行';
const DEFAULT_DESCRIPTION = 'vivo社区App是vivo与用户的沟通交流平台，也是V粉的大本营! 在vivo社区App可以获取最新产品资讯、交流摄影心得、切磋玩机技巧、反馈使用体验、赢取专属福利等。上vivo社区App，与热爱同行！';
const DEFAULT_KEYWORDS = 'vivo社区,vivo社区App,V粉,vivo官方论坛,手机摄影技巧,玩机教程,vivo新品资讯,问题反馈,vivo福利,vivo活动';

export const DEFAULT_SEO: Required<SEOConfig> = {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    keywords: DEFAULT_KEYWORDS,
};

const TOPIC_SEO_FALLBACK_SUFFIX = '最新动态与讨论 - vivo社区App';
const THREAD_TITLE_SUFFIX = ' - vivo社区App';

function ensureMetaTag(name: string) {
    let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
    if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
    }
    return meta;
}

function normalizeText(value: any) {
    return String(value || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/\s+/g, ' ')
        .trim();
}

function truncateText(value: string, maxLength: number) {
    if (!value) return '';
    return value.length > maxLength ? value.slice(0, maxLength) : value;
}

function uniqueStrings(values: Array<any>) {
    return Array.from(new Set(values.map(item => normalizeText(item)).filter(Boolean)));
}

function extractTopicNames(threadData: any) {
    return uniqueStrings((threadData?.topics || []).map((topic: any) => topic?.name));
}

function extractDeviceKeyword(threadData: any) {
    const candidates = [
        threadData?.device,
        threadData?.deviceName,
        threadData?.model,
        threadData?.modelName,
        threadData?.phoneModel,
        threadData?.source,
    ];

    return uniqueStrings(candidates).find(item => /(vivo|iqoo)/i.test(item)) || '';
}

export function applySEO(config: SEOConfig = {}) {
    if (typeof document === 'undefined') return;

    const title = normalizeText(config.title) || DEFAULT_SEO.title;
    const description = normalizeText(config.description) || DEFAULT_SEO.description;
    const keywords = normalizeText(config.keywords) || DEFAULT_SEO.keywords;

    document.title = title;
    ensureMetaTag('description').setAttribute('content', description);
    ensureMetaTag('keywords').setAttribute('content', keywords);
}

export function applyDefaultSEO(overrides: SEOConfig = {}) {
    applySEO({
        ...DEFAULT_SEO,
        ...overrides,
    });
}

export function buildThreadDetailSEO(threadData: any): SEOConfig {
    const authorName = normalizeText(threadData?.user?.nickname) || '用户';
    const plainContent = normalizeText(threadData?.content || threadData?.summary || '');
    const titleText = normalizeText(threadData?.title) || truncateText(plainContent, 30);
    const contentPreview = truncateText(plainContent, 100);
    const postCount = threadData?.postCount || 0;

    const title = titleText
        ? `${titleText} - vivo社区App`
        : `vivo社区App`;

    const descriptionPrefix = `${authorName}在vivo社区App分享：`;
    let description = descriptionPrefix + contentPreview;
    if (description.length > 100) {
        description += '...';
    }
    if (postCount > 0) {
        description += ` 查看 ${postCount} 条评论。`;
    }

    const keywordParts = uniqueStrings([
        'vivo社区',
        'vivo社区App',
        'V粉',
        extractDeviceKeyword(threadData),
        ...extractTopicNames(threadData),
    ]);

    return {
        title,
        description,
        keywords: keywordParts.join(','),
    };
}

export function buildTopicDetailSEO(topicData: any): SEOConfig {
    const topicName = normalizeText(topicData?.name);
    if (!topicName) {
        return DEFAULT_SEO;
    }

    const threadCount = topicData?.threadCount || topicData?.postCount || topicData?.post_count || 0;
    const discussionCount = topicData?.discussionCount || 0;
    const followCount = topicData?.followCount || 0;

    const title = `${topicName} (${threadCount}篇帖子,${discussionCount}条讨论) - 最新动态与讨论 - vivo社区App`;
    const description = `vivo社区App${topicName}专区，已有${followCount}人关注，累计${threadCount}篇帖子和${discussionCount}条讨论。这里汇聚了V粉关于${topicName}的最新动态、使用心得与讨论。加入vivo社区，与万千V粉一起交流${topicName}！`;
    const keywords = uniqueStrings([
        'vivo社区',
        'vivo社区App',
        topicName,
        `${topicName}怎么样`,
        `${topicName}教程`,
        `${topicName}资讯`,
    ]).join(',');

    return {
        title,
        description,
        keywords,
    };
}